import React, { useState, useMemo } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { useGlobalContext } from "../../../Config";
import {
  guardarConfiguracionPredeterminada,
  actualizarConfiguracionDetalles,
} from "../../../services/ClienteService.js";
import {
  cantidadPorTipo,
  distribuirPorCantidad,
  normalizarDocumentos,
  validarAsignacionDocumentos,
} from "../../../utilities/documentosCobro.js";
import TablaCobroCategoria from "../../../shared/components/documentos/TablaCobroCategoria.js";
import BoxEscenarioDocumentos from "../../../shared/components/documentos/BoxEscenarioDocumentos.js";

// Reconfiguración de documentos de venta de un cliente desde Agrupación:
// box con la cantidad de documentos por tipo (1..N dinámica) + una sección
// por asignación vigente/futura donde cada ítem se asigna obligatoriamente a
// uno de esos documentos. La numeración es por tipo ("Factura Exenta N") y
// cruza asignaciones. El estado consolidado/separado no viaja desde el
// front: el backend lo infiere de la distribución.
// Persistencia: guardarConfiguracionPredeterminada (config del cliente) +
// actualizarConfiguracionDetalles (lote de ítems editados; el backend solo
// acepta ítems de asignaciones en estado ASIGNACIÓN).
const DialogReconfiguracionCliente = ({
  cliente,
  catalogo,
  visible,
  onHide,
  onGuardado,
}) => {
  const { baseUrl } = useGlobalContext();

  const catalogoTipos = Array.isArray(catalogo) ? catalogo : [];
  const categorias = catalogoTipos
    .map((tipo) => tipo.descripcion)
    .filter(Boolean);

  const asignaciones = useMemo(
    () =>
      Array.isArray(cliente?.asignaciones) ? cliente.asignaciones : [],
    [cliente]
  );

  // Snapshot original de los ítems: agrupamiento = número de documento
  // asignado (null/0 = sin asignar)
  const itemsExistentesBase = useMemo(
    () =>
      asignaciones.flatMap((asig) =>
        (Array.isArray(asig.detalles) ? asig.detalles : []).map((d) => ({
          id: d.id,
          descripcion: d.descripcion,
          relacion: d.relacion,
          cobro: d.cobro,
          agrupamiento: Number(d.agrupamiento) || null,
          asignacionId: asig.id,
          vigencia: asig.vigencia,
          existente: true,
        }))
      ),
    [asignaciones]
  );

  const [configEditable, setConfigEditable] = useState([]);
  const [itemsExistentes, setItemsExistentes] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const totalesPorCobro = useMemo(() => {
    const acc = {};
    categorias.forEach((cat) => {
      acc[cat] = { total: 0, nuevos: 0, existentes: 0 };
    });
    itemsExistentes.forEach((i) => {
      const t = acc[i.cobro];
      if (!t) return;
      t.total += 1;
      t.existentes += 1;
    });
    return acc;
  }, [itemsExistentes, categorias]);

  // Cantidad de documentos vigente por tipo
  const cantidadesPorCobro = useMemo(
    () => cantidadPorTipo(configEditable),
    [configEditable]
  );

  // Dropdowns "Documento N" por tipo: exactamente las opciones definidas en
  // el box (valores 1..cantidad, numeración por tipo)
  const optionsPorCobro = useMemo(() => {
    const map = {};
    Object.entries(cantidadesPorCobro).forEach(([cat, cantidad]) => {
      map[cat] = Array.from({ length: Math.max(cantidad ?? 0, 0) }, (_, idx) => ({
        label: `${cat} ${idx + 1}`,
        value: idx + 1,
      }));
    });
    return map;
  }, [cantidadesPorCobro]);

  const cambiosPendientes = useMemo(
    () =>
      itemsExistentes.filter((i) => {
        const original = itemsExistentesBase.find((b) => b.id === i.id);
        if (!original) return false;
        return (
          (Number(original.agrupamiento) || 0) !==
          (Number(i.agrupamiento) || 0)
        );
      }),
    [itemsExistentes, itemsExistentesBase]
  );

  const handleShow = () => {
    const baseItems = itemsExistentesBase.map((i) => ({ ...i }));
    const conteoGlobal = baseItems.reduce((acc, i) => {
      acc[i.cobro] = (acc[i.cobro] || 0) + 1;
      return acc;
    }, {});
    const configuracionGuardada = Array.isArray(
      cliente?.configuracionPredeterminada
    )
      ? cliente.configuracionPredeterminada
      : [];

    // Cantidad por tipo: la guardada en BD (clampeada al total de ítems); si
    // no existe u quedó obsoleta se deriva del total (1 documento por ítem)
    const configInicial = catalogoTipos.map((t) => {
      const descripcion = t.descripcion;
      const codigo = t.codigo ?? descripcion;
      const total = Math.max(conteoGlobal[descripcion] ?? 0, 1);
      const guardada = configuracionGuardada.find(
        (c) =>
          c.codigo === codigo ||
          (t.codigo == null && (c.descripcion ?? c.documentoCobro) === descripcion)
      );
      const guardadaCantidad = Number(guardada?.cantidadDocumentos);
      const cantidad =
        Number.isFinite(guardadaCantidad) && guardadaCantidad >= 1
          ? Math.min(guardadaCantidad, total)
          : total;
      return { codigo, descripcion, cantidadDocumentos: cantidad };
    });

    setConfigEditable(configInicial);
    // Conserva la distribución guardada si es válida; re-distribuye los tipos
    // con valores legacy/obsoletos (fuera del rango 1..cantidad)
    setItemsExistentes(
      normalizarDocumentos(baseItems, cantidadPorTipo(configInicial))
    );
  };

  // Al cambiar la cantidad de un tipo, sus ítems se re-distribuyen
  // round-robin en los nuevos documentos
  const handleCambiarConfig = (codigo, descripcion, patch) => {
    const nuevaConfig = (configEditable || []).map((c) =>
      c.codigo === codigo ? { ...c, ...patch } : c
    );
    setConfigEditable(nuevaConfig);
    setItemsExistentes((prev) =>
      distribuirPorCantidad(prev, cantidadPorTipo(nuevaConfig), descripcion)
    );
  };

  const handleLimpiarAgrupaciones = () => {
    if (!window.confirm("¿Está seguro que desea reiniciar la configuración?")) {
      return;
    }
    setItemsExistentes((prev) =>
      distribuirPorCantidad(prev, cantidadPorTipo(configEditable))
    );
  };

  function onAsignarDocumento(rowData, e) {
    setItemsExistentes((prev) =>
      prev.map((i) =>
        i.id === rowData.id ? { ...i, agrupamiento: e.value ?? null } : i
      )
    );
  }

  const validarTodo = () => {
    const error = validarAsignacionDocumentos(
      itemsExistentes,
      cantidadesPorCobro
    );
    if (!error) return null;
    return `${error} Puedes cambiar la cantidad de documentos en el box o corregir la asignación de los ítems.`;
  };

  const handleGuardar = async () => {
    // La configuración es por (cliente, empresa): sin idEmpresa no se persiste
    if (!Number(cliente?.idEmpresa)) {
      window.alert(
        "No se pudo determinar la empresa de la combinación. No es posible guardar la configuración."
      );
      return;
    }
    const error = validarTodo();
    if (error) {
      window.alert(`⚠️ ${error}`);
      return;
    }
    setGuardando(true);
    try {
      await guardarConfiguracionPredeterminada(baseUrl, {
        idCliente: cliente.id,
        idEmpresa: Number(cliente.idEmpresa),
        configuracion: (configEditable || []).map((c) => ({
          codigo: c.codigo,
          cantidadDocumentos: Number(c.cantidadDocumentos) || 1,
        })),
      });

      // La validación garantiza agrupamiento >= 1; cualquier caso que escape
      // debe fallar visible en el backend, no omitirse en silencio
      const editados = cambiosPendientes.map((i) => ({
        id: i.id,
        agrupamiento: Number(i.agrupamiento),
      }));
      if (editados.length > 0) {
        await actualizarConfiguracionDetalles(baseUrl, { detalles: editados });
      }

      onGuardado?.();
    } catch (error) {
      window.alert(error?.message || "No se pudo guardar la configuración.");
    } finally {
      setGuardando(false);
    }
  };

  const handleVolver = () => {
    if (
      cambiosPendientes.length === 0 ||
      window.confirm(
        "¿Está seguro que desea volver? Se perderá la configuración ingresada."
      )
    ) {
      onHide?.();
    }
  };

  const nombreCliente =
    cliente?.alias ||
    cliente?.razon_social ||
    cliente?.nombre_fantasia ||
    "Cliente";

  const tiposConItems = (asig) =>
    categorias.filter((cat) =>
      itemsExistentes.some(
        (i) => i.asignacionId === asig.id && i.cobro === cat
      )
    );

  return (
    <Dialog
      visible={visible}
      onHide={handleVolver}
      modal
      className="dialog-responsive"
      style={{
        maxWidth: window.innerWidth <= 600 ? "95vw" : "75vw",
        width: window.innerWidth <= 600 ? "95%" : "90%",
        margin: window.innerWidth <= 600 ? "2.5vw" : "auto",
      }}
      onShow={handleShow}
    >
      {guardando ? (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <i
            className="pi pi-spin pi-spinner"
            style={{ fontSize: "2rem" }}
          ></i>
          <div style={{ marginTop: "10px" }}>Guardando configuración...</div>
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center", position: "relative" }}>
            <h2>Configuración de Documentos de Venta</h2>
            <div className="text-600 text-xl mb-2">
              {nombreCliente}
              {cliente?.rut ? ` · RUT ${cliente.rut}` : ""}
              {cliente?.empresa ? ` · Empresa ${cliente.empresa}` : ""}
            </div>
          </div>

          <BoxEscenarioDocumentos
            catalogo={catalogo}
            config={configEditable}
            totalesPorCobro={totalesPorCobro}
            onChangeConfig={handleCambiarConfig}
            disabled={guardando}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "16px",
            }}
          >
            <h6 className="mt-3" style={{ margin: 0 }}>
              • Asignación de documentos por asignación (vigentes y futuras)
              {cambiosPendientes.length > 0 && (
                <Tag
                  value={`${cambiosPendientes.length} cambio${cambiosPendientes.length === 1 ? "" : "s"} sin guardar`}
                  severity="warn"
                  className="ml-2 p-tag-sm"
                />
              )}
            </h6>
            <Button
              icon="pi pi-refresh"
              severity="primary"
              outlined
              className="p-button-sm"
              onClick={handleLimpiarAgrupaciones}
              tooltip="Reiniciar configuración según el box"
              tooltipOptions={{ position: "left" }}
              disabled={guardando}
            />
          </div>

          {asignaciones.length === 0 ? (
            <div className="p-3 text-600">
              El cliente no tiene asignaciones vigentes ni futuras.
            </div>
          ) : (
            asignaciones.map((asig) => {
              const tipos = tiposConItems(asig);
              return (
                <div className="card mb-3" key={asig.id}>
                  <div
                    className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-2"
                  >
                    <h6 className="m-0 flex align-items-center gap-2 flex-wrap">
                      <strong>Folio {asig.folio ?? "asig.id"}</strong>
                      <span className="text-600 text-sm">
                        · ID {asig.id}
                      </span>
                      <span>
                        · <strong>{asig.empresa ?? "—"}</strong>
                      </span>
                    </h6>
                    <div className="flex align-items-center gap-2">
                      <Tag
                        value={asig.vigencia === "futura" ? "Futura" : "Vigente"}
                        severity={
                          asig.vigencia === "futura" ? "info" : "success"
                        }
                        className="p-tag-sm"
                      />
                      <span className="text-600 text-sm">
                        Vigencia: {asig.desde ?? "—"} → {asig.hasta ?? "—"}
                      </span>
                    </div>
                  </div>

                  {tipos.length === 0 ? (
                    <span className="text-500 text-sm">
                      Sin ítems para esta asignación.
                    </span>
                  ) : (
                    tipos.map((cat) => {
                      return (
                        <TablaCobroCategoria
                          key={cat}
                          categoria={cat}
                          visible={true}
                          items={itemsExistentes
                            .filter(
                              (i) =>
                                i.asignacionId === asig.id && i.cobro === cat
                            )
                            .sort(
                              (a, b) =>
                                (Number(a.agrupamiento) || 9999) -
                                  (Number(b.agrupamiento) || 9999) ||
                                String(a.descripcion ?? "").localeCompare(
                                  String(b.descripcion ?? "")
                                )
                            )}
                          loadingAsig={false}
                          options={optionsPorCobro[cat] ?? []}
                          onAsignarDocumento={onAsignarDocumento}
                        />
                      );
                    })
                  )}
                </div>
              );
            })
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              gap: "1rem",
              marginTop: "10px",
            }}
          >
            <Button
              label="Volver"
              icon="pi pi-arrow-left"
              className="mt-2 mb-2 p-button-rounded p-button-md p-button"
              onClick={handleVolver}
              outlined
              disabled={guardando}
              raised
            />
            <Button
              label="Guardar"
              icon="pi pi-save"
              className="p-button-success mt-2 mb-2 p-button-rounded p-button-md p-button"
              onClick={handleGuardar}
              disabled={guardando}
              raised
            />
          </div>
        </>
      )}
    </Dialog>
  );
};

export default DialogReconfiguracionCliente;